/** Knowledge graph V2 API service (kg schema). */
import { apiGet } from "./httpClient";
import type {
  GraphData,
  KgBuildingSearchResult,
  KgNode,
  NodeDetail,
  OneHopResult,
  TwoHopResult
} from "@/types/kg";

// 首次欢迎页与星云分块并行加载时，提前请求一次总览图谱。
// 结果只交给下一次 fetch 消费，后续图谱更新仍会正常重新请求。
let warmedOverviewGraph: GraphData | null = null;
let warmingOverviewGraph: Promise<GraphData> | null = null;

export function preloadKgOverviewGraph(): Promise<GraphData> {
  if (warmedOverviewGraph) return Promise.resolve(warmedOverviewGraph);
  if (!warmingOverviewGraph) {
    warmingOverviewGraph = apiGet<GraphData>("/kg/graph/overview")
      .then((graph) => {
        warmedOverviewGraph = graph;
        return graph;
      })
      .finally(() => {
        warmingOverviewGraph = null;
      });
  }
  return warmingOverviewGraph;
}

export function fetchKgNode(nodeId: string, signal?: AbortSignal): Promise<KgNode> {
  return apiGet(`/kg/nodes/${encodeURIComponent(nodeId)}`, signal);
}

export function fetchKgNodeDetail(
  nodeId: string,
  params?: { relationFamily?: string; discoveryType?: string; oneHopLimit?: number; twoHopLimit?: number },
  signal?: AbortSignal
): Promise<NodeDetail> {
  const sp = new URLSearchParams();
  if (params?.relationFamily) sp.set("relationFamily", params.relationFamily);
  if (params?.discoveryType) sp.set("discoveryType", params.discoveryType);
  if (params?.oneHopLimit !== undefined) sp.set("oneHopLimit", String(params.oneHopLimit));
  if (params?.twoHopLimit !== undefined) sp.set("twoHopLimit", String(params.twoHopLimit));
  const qs = sp.toString();
  return apiGet(`/kg/nodes/${encodeURIComponent(nodeId)}/detail${qs ? "?" + qs : ""}`, signal);
}

export function fetchKgGraph(
  centerNodeId: string,
  depth: 1 | 2 = 1,
  signal?: AbortSignal
): Promise<GraphData> {
  return apiGet(`/kg/graph?centerNodeId=${encodeURIComponent(centerNodeId)}&depth=${depth}`, signal);
}

export function fetchKgOverviewGraph(signal?: AbortSignal): Promise<GraphData> {
  if (warmedOverviewGraph) {
    const graph = warmedOverviewGraph;
    warmedOverviewGraph = null;
    return Promise.resolve(graph);
  }
  if (warmingOverviewGraph) return warmingOverviewGraph;
  return apiGet("/kg/graph/overview", signal);
}

export async function fetchKgOneHop(
  nodeId: string,
  params?: { relationFamily?: string; limit?: number },
  signal?: AbortSignal
): Promise<OneHopResult[]> {
  const sp = new URLSearchParams();
  if (params?.relationFamily) sp.set("relationFamily", params.relationFamily);
  if (params?.limit !== undefined) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  const payload = await apiGet<{ edges: OneHopResult[]; total: number }>(
    `/kg/nodes/${encodeURIComponent(nodeId)}/one-hop${qs ? "?" + qs : ""}`,
    signal
  );
  return payload.edges;
}

export async function fetchKgTwoHop(
  nodeId: string,
  params?: { discoveryType?: string; limit?: number },
  signal?: AbortSignal
): Promise<TwoHopResult[]> {
  const sp = new URLSearchParams();
  if (params?.discoveryType) sp.set("discoveryType", params.discoveryType);
  if (params?.limit !== undefined) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  const payload = await apiGet<{ discoveries: TwoHopResult[]; total: number }>(
    `/kg/nodes/${encodeURIComponent(nodeId)}/two-hop${qs ? "?" + qs : ""}`,
    signal
  );
  return payload.discoveries;
}

export async function searchKgNodes(
  query: string,
  type?: string,
  limit = 20,
  signal?: AbortSignal,
  options: { preferBuildings?: boolean } = {}
): Promise<KgNode[]> {
  const sp = new URLSearchParams();
  sp.set("q", query);
  if (type) sp.set("type", type);
  sp.set("limit", String(limit));
  if (options.preferBuildings) sp.set("preferBuildings", "true");
  const payload = await apiGet<{ results: KgNode[]; total: number }>(`/kg/search?${sp}`, signal);
  return payload.results;
}

export async function searchKgBuildings(
  query: string,
  limit = 20,
  signal?: AbortSignal
): Promise<KgBuildingSearchResult[]> {
  const sp = new URLSearchParams();
  sp.set("q", query);
  sp.set("limit", String(limit));
  const payload = await apiGet<{ results: KgBuildingSearchResult[]; total: number }>(
    `/kg/search/buildings?${sp}`,
    signal
  );
  return payload.results;
}

export function fetchKgStats(signal?: AbortSignal): Promise<{
  nodeTypes: Array<{ node_type: string; cnt: number }>;
  edgeTypes: Array<{ relation_type: string; cnt: number }>;
  discoveryTypes: Array<{ discovery_type: string; cnt: number }>;
  totalNodes: number; totalEdges: number; totalDiscoveries: number;
}> {
  return apiGet("/kg/stats", signal);
}
